import $ from 'jquery';

import webApp from './webApp';
import issueCache from './issueCache';
import i18n from './i18n';

class IssueService {

  constructor() {
    this.pageHtmlRequests = {};
    this.previewPages = [];
  }

  requestIssue( issueId ) {
    let url = webApp.env.site_url +'/asmag/web-app/issue/'+ issueId;
    const queryVars = this._getUrlQueryVars();

    // See if the current URL contains an issue share token or a page share token,
    // which gives the user permission to read the whole issue, or specific pages

    if ( queryVars[ 'shared-issue' ] ) {
      url += '?shared-issue='+ encodeURIComponent( queryVars[ 'shared-issue' ] );
    }
    // Support for legacy issue share URLs
    // TODO: Remove this when Asteikko Mag v6 has been in production use for a year or so
    else if ( queryVars[ 'share' ] && queryVars[ 'ts' ] ) {
      url += '?shared-issue='+ issueId +'-'+ encodeURIComponent( queryVars[ 'share' ] ) +'-'+ encodeURIComponent( queryVars[ 'ts' ] );
    }
    else if ( queryVars[ 'shared-page' ] ) {
      url += '?shared-page='+ encodeURIComponent( queryVars[ 'shared-page' ] );
    }
    else if ( this.previewPages.length ) {
      url += '?preview-pages='+ this.previewPages.join( ',' );
    }

    // Note: we skip the browser's cache if the current user is a WP editor or admin
    const xhr = $.ajax( url, { cache: !webApp.user.isEditor } );

    return Promise.resolve( xhr ).
      then( issue => {

        // Don't save issue data to cache for editors and admins, since they
        // always want to see fresh data
        if ( !webApp.user.isEditor && issue.permissions.read ) {
          // We probably can't fit multiple issues into localStorage
          issueCache.clearCacheForOtherIssuesThan( issueId );
          issueCache.saveIssueMeta( issueId, issue );
        }

        return issue;
      } ).
      catch( reason => {

        // Don't get issue data from cache for editors and admins, since they
        // always want to see fresh data
        if ( !webApp.user.isEditor ) {
          webApp.console.error( reason );
          const cachedIssue = issueCache.getIssueMeta( issueId );

          if ( cachedIssue )
            return cachedIssue;
          else
            throw reason;
        }
        else {
          throw reason;
        }

      } );
  }

  requestIssueCss( issue ) {
    const cachedIssue = issueCache.getIssueMeta( issue.id );
    const cachedCss = issueCache.getIssueCss( issue.id );

    // Issue has not been updated. Use the version from localStorage.
    if ( cachedIssue && cachedCss && issue.revision == cachedIssue.revision && !webApp.user.isEditor ) {
      return Promise.resolve( cachedCss );
    }
    // Issue is not in cache, or the cache is stale. Fetch new CSS from server.
    else {

      if ( !issue.css_url )
        return Promise.reject( 'Issue has no css_url' );

      // Note: we skip the browser's cache if the current user is a WP editor or admin
      const xhr = $.ajax( issue.css_url, { cache: !webApp.user.isEditor, dataType: 'text' } );

      return Promise.resolve( xhr ).then( css => {
        // Editors and admins always want to see fresh data, so there's no need
        // to cache CSS for them
        if ( !webApp.user.isEditor && issue.permissions.read ) {
          issueCache.saveIssueCss( issue.id, css );
        }

        return css;
      } );
    }
  }

  /**
   * @return {Promise}
   */
  requestPageHtml( pageIndex, page, issue ) {

    this.cancelPageHtmlRequest( pageIndex );

    if ( !page.html_url ) {
      webApp.console.warn( 'Page has no html_url' );
      return Promise.resolve( '' );
    }

    const cachedHtml = this._getCachedHtml( pageIndex, issue );

    if ( cachedHtml )
      return Promise.resolve( cachedHtml );

    if ( !webApp.networkStatus )
      return Promise.reject( 'App is offline' );

    var xhr = this.pageHtmlRequests[ pageIndex ] = $.ajax( {
      url: page.html_url,
      dataType: 'html',
    // Note: we skip the browser's cache if the current user is a WP editor or admin
      cache: !webApp.user.isEditor,
    } );

    // Convert the jQuery Deferred into an ES6 Promise
    return Promise.resolve( xhr ).
      then( html => {
        delete this.pageHtmlRequests[ pageIndex ];

        if ( !webApp.user.isEditor && issue.permissions.read ) {
          issueCache.savePage( issue.id, page.id, html, page.modified );
        }

        return html;
      } ).
      catch( reason => {
        delete this.pageHtmlRequests[ pageIndex ];

        // If the server returns "401 Gone", then the page HTML URL has expired.
        // We'll re-open the issue to get fresh page HTML URLs.
        if ( reason.status && reason.status == 410 ) {
          const currentPageId = webApp.issueReader.currentPage.id;

          clearTimeout( this.reOpenTimeout );

          this.reOpenTimeout = setTimeout( () => {
            webApp.issueReader.closeIssue().then( () => {
              webApp.issueReader.openIssue( issue.id, currentPageId );
            } );
          }, 500 );
        }

        throw reason;
      } );
  }

  /**
   * @return {Boolean}
   */
  cancelPageHtmlRequest( pageIndex ) {

    if ( !this.pageHtmlRequests[ pageIndex ] )
      return false;

    var request = this.pageHtmlRequests[ pageIndex ];
    delete this.pageHtmlRequests[ pageIndex ];
    request.abort();

    return true;
  }

  _getCachedHtml( index, issue ) {

    if ( !issue.pages[ index ] )
      return null;

    if ( !webApp.user.isEditor ) {

      var cachedPage = issueCache.getPage( issue.id, issue.pages[ index ].id );

      // We compare the timestamps of the locally cached page, and the page in
      // the issue.pages array. If they differ, it means that our locally
      // cached page data is stale, and we will instead load this page from
      // the server (unless the browser is offline, in which case we use the
      // locally cached page content).
      if ( cachedPage && ( !webApp.networkStatus || String( cachedPage.modified ) === String( issue.pages[ index ].modified ) ) ) {
        webApp.console.log( 'Returned up-to-date page '+ index +' from local cache' );
        return cachedPage.content;
      }
      else if ( !webApp.networkStatus ) {
        webApp.console.log( 'Page '+ index +' not in local cache, or cache is stale, and the browser is not online' );
        return '<div class="asmag-noncachedpage">'+ i18n.strings.base.offline.pagenotcached +'</div>';
      }
    }

    webApp.console.log( 'Page '+ index +' not in local cache, or cache is stale. Page will be downloaded from the server.' );

    return null;
  }

  _getUrlQueryVars() {
    return (function(a) {
      if (a == "") return {};
      var b = {};
      for (var i = 0; i < a.length; ++i)
      {
        var p=a[i].split('=', 2);
        if (p.length == 1)
          b[p[0]] = "";
        else
          b[p[0]] = decodeURIComponent(p[1].replace(/\+/g, " "));
      }
      return b;
    })(window.location.search.substr(1).split('&'));
  }
}

export default ( new IssueService() );
